import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Search, ChevronUp, ChevronDown, AlertTriangle, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { authFetch } from "./api";

const API = 'https://hs.aida-advisory.com';

const eligColor = s => ({ eligible: '#00E676', at_risk: '#FFB300', ineligible: '#EF5350' }[s] || '#546E7A');
const eligBg    = s => ({ eligible: '#00E67615', at_risk: '#FFB30015', ineligible: '#EF535015' }[s] || '#546E7A15');
const eligLabel = s => ({ eligible: 'Eligible', at_risk: 'At Risk', ineligible: 'Ineligible' }[s] || 'Unknown');
const eligIcon  = s => {
  if (s === 'eligible')   return <CheckCircle size={14} />;
  if (s === 'at_risk')    return <AlertTriangle size={14} />;
  if (s === 'ineligible') return <XCircle size={14} />;
  return null;
};

const gpaColor = g => {
  if (!g) return '#546E7A';
  const v = parseFloat(g);
  if (v >= 3.5) return '#00E676';
  if (v >= 2.5) return '#FFD600';
  return '#EF5350';
};

const AcademicTracking = () => {
  const navigate = useNavigate();
  const [players, setPlayers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filterElig, setFilterElig] = useState('all');
  const [sortField, setSortField]   = useState('name');
  const [sortDir, setSortDir]       = useState('asc');

  const loadWatchlist = async () => {
    setLoading(true);
    try {
      const list = JSON.parse(localStorage.getItem('academic_watchlist') || '[]');
      if (list.length === 0) { setPlayers([]); return; }
      // Fetch full details for each player on the watchlist
      const results = await Promise.all(
        list.map(w => authFetch(`${API}/api/players/${w.id}`).then(r => r.ok ? r.json() : null))
      );
      setPlayers(results.filter(Boolean));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadWatchlist(); }, []);

  const removeFromWatchlist = (playerId) => {
    try {
      const list = JSON.parse(localStorage.getItem('academic_watchlist') || '[]');
      const updated = list.filter(p => p.id !== playerId);
      localStorage.setItem('academic_watchlist', JSON.stringify(updated));
      setPlayers(prev => prev.filter(p => p.id !== playerId));
    } catch(e) { console.error(e); }
  };

  const handleSort = field => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = players
    .filter(p => {
      const q = search.toLowerCase();
      const matchSearch = !q || p.name?.toLowerCase().includes(q) || p.high_school?.toLowerCase().includes(q) || p.position?.toLowerCase().includes(q);
      const matchElig = filterElig === 'all' || p.ncaa_eligibility === filterElig;
      return matchSearch && matchElig;
    })
    .sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (sortField === 'gpa') { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
      else if (sortField === 'sat_score' || sortField === 'act_score') { av = parseInt(av) || 0; bv = parseInt(bv) || 0; }
      else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  // Summary stats
  const total      = players.length;
  const eligible   = players.filter(p => p.ncaa_eligibility === 'eligible').length;
  const atRisk     = players.filter(p => p.ncaa_eligibility === 'at_risk').length;
  const ineligible = players.filter(p => p.ncaa_eligibility === 'ineligible').length;
  const avgGpa     = players.filter(p => p.gpa).length > 0
    ? (players.filter(p => p.gpa).reduce((s, p) => s + parseFloat(p.gpa), 0) / players.filter(p => p.gpa).length).toFixed(2)
    : '—';

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp size={12} className="opacity-20" />;
    return sortDir === 'asc' ? <ChevronUp size={12} style={{ color: '#00D4FF' }} /> : <ChevronDown size={12} style={{ color: '#00D4FF' }} />;
  };

  const Th = ({ field, children, align = 'left' }) => (
    <th
      onClick={() => handleSort(field)}
      className="px-4 py-3 cursor-pointer select-none hover:text-white transition-colors"
      style={{ textAlign: align, color: sortField === field ? '#00D4FF' : '#546E7A', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}
    >
      <span className="flex items-center gap-1" style={{ justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
        {children} <SortIcon field={field} />
      </span>
    </th>
  );

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-6 pb-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-5 md:mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 rounded-lg" style={{ background: '#7E57C220', border: '1px solid #7E57C240' }}>
                <GraduationCap size={22} style={{ color: '#7E57C2' }} />
              </div>
              <h1 className="text-white text-2xl font-bold">Academic Tracking</h1>
            </div>
            <p className="text-[#546E7A] text-xs md:text-sm md:ml-12">Academic watchlist — prospects added from their Player Profile</p>
          </div>
          <button onClick={() => navigate('/')} className="btn-secondary text-sm hidden md:block flex-shrink-0">← Home</button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 xs:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4 mb-5 md:mb-6">
          {[
            { label: 'Total Prospects', value: total,      color: '#00D4FF' },
            { label: 'NCAA Eligible',   value: eligible,   color: '#00E676' },
            { label: 'At Risk',         value: atRisk,     color: '#FFB300' },
            { label: 'Ineligible',      value: ineligible, color: '#EF5350' },
            { label: 'Average GPA',     value: avgGpa,     color: gpaColor(avgGpa) },
          ].map(s => (
            <div key={s.label} className="panel-card text-center py-4">
              <div style={{ color: s.color, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }} className="text-2xl md:text-[32px]">{s.value}</div>
              <div style={{ color: '#546E7A', fontSize: 11, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative w-full md:flex-1 md:max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#546E7A]" />
            <input
              type="text"
              placeholder="Search by name, school, position..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-[#0A1628] text-white pl-9 pr-4 py-2 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none text-sm"
            />
          </div>
          <div className="chip-row md:flex md:gap-3">
          {['all', 'eligible', 'at_risk', 'ineligible'].map(f => (
            <button
              key={f}
              onClick={() => setFilterElig(f)}
              className="px-4 py-2 rounded-lg text-sm font-semibold border transition-colors whitespace-nowrap flex-shrink-0"
              style={{
                background: filterElig === f ? `${eligColor(f === 'all' ? null : f)}20` : 'transparent',
                borderColor: filterElig === f ? `${eligColor(f === 'all' ? null : f)}60` : '#546E7A30',
                color: filterElig === f ? (f === 'all' ? '#00D4FF' : eligColor(f)) : '#546E7A',
              }}
            >
              {f === 'all' ? 'All' : eligLabel(f)}
            </button>
          ))}
          </div>
        </div>

        {/* Table */}
        <div className="panel-card p-0 overflow-hidden">
          {loading ? (
            <div className="text-center py-16 text-[#00D4FF]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-[#546E7A] mb-2">No prospects on your academic watchlist.</div>
              <div className="text-[#546E7A] text-sm">Go to a player's Profile tab and click "Add to Academic Tracking".</div>
            </div>
          ) : (
            <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }} className="desktop-only">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(84,110,122,0.2)', background: 'rgba(10,22,40,0.6)' }}>
                  <Th field="name">Name</Th>
                  <Th field="position">Pos</Th>
                  <Th field="class_year">Class</Th>
                  <Th field="high_school">High School</Th>
                  <Th field="gpa" align="center">GPA</Th>
                  <Th field="ncaa_eligibility" align="center">NCAA Status</Th>
                  <Th field="sat_score" align="center">SAT</Th>
                  <Th field="act_score" align="center">ACT</Th>
                  <Th field="graduation_year" align="center">Grad Year</Th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => (
                  <tr
                    key={p.id}
                    style={{ borderBottom: '1px solid rgba(84,110,122,0.1)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)', cursor: 'pointer' }}
                    className="hover:bg-[#0A1628] transition-colors"
                    onClick={() => navigate(`/players/${p.id}/fit?tab=profile`)}
                  >
                    <td className="px-4 py-3">
                      <div style={{ color: '#FFFFFF', fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                      {(p.hometown || p.hs_state) && <div style={{ color: '#546E7A', fontSize: 11 }}>{[p.hometown, p.hs_state ? (p.hs_state.length > 2 ? p.hs_state.substring(0,2).toUpperCase() : p.hs_state.toUpperCase()) : null].filter(Boolean).join(', ')}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span style={{ background: '#00D4FF20', border: '1px solid #00D4FF40', color: '#00D4FF', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
                        {p.position || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: '#B0BEC5', fontSize: 13 }}>{p.class_year || '—'}</td>
                    <td className="px-4 py-3" style={{ color: '#B0BEC5', fontSize: 13 }}>{p.high_school || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span style={{ color: gpaColor(p.gpa), fontWeight: 700, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
                        {p.gpa ? parseFloat(p.gpa).toFixed(2) : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: eligBg(p.ncaa_eligibility),
                        border: `1px solid ${eligColor(p.ncaa_eligibility)}40`,
                        color: eligColor(p.ncaa_eligibility),
                        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                      }}>
                        {eligIcon(p.ncaa_eligibility)}
                        {eligLabel(p.ncaa_eligibility)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center" style={{ color: '#B0BEC5', fontSize: 13 }}>{p.sat_score || '—'}</td>
                    <td className="px-4 py-3 text-center" style={{ color: '#B0BEC5', fontSize: 13 }}>{p.act_score || '—'}</td>
                    <td className="px-4 py-3 text-center" style={{ color: '#B0BEC5', fontSize: 13 }}>{p.graduation_year || '—'}</td>
                    <td className="px-4 py-3">
                      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10}}>
                        <button
                          onClick={e => { e.stopPropagation(); navigate(`/players/${p.id}/fit?tab=profile`); }}
                          style={{ color: '#00D4FF', fontSize: 12, fontWeight: 600 }}
                          className="hover:underline"
                        >
                          View →
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); removeFromWatchlist(p.id); }}
                          style={{ color: '#546E7A', display:'flex', alignItems:'center' }}
                          title="Remove from watchlist"
                          onMouseEnter={e => e.currentTarget.style.color='#EF5350'}
                          onMouseLeave={e => e.currentTarget.style.color='#546E7A'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile: nine columns of academic data become a card per prospect */}
            <div className="mobile-only p-3 space-y-2.5">
              {filtered.map((p) => (
                <div
                  key={p.id}
                  onClick={() => navigate(`/players/${p.id}/fit?tab=profile`)}
                  className="list-card"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-white font-semibold truncate">{p.name}</div>
                      <div className="text-[#B0BEC5] text-xs mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span className="text-[#00D4FF] font-semibold">{p.position || '—'}</span>
                        {p.class_year && <span>• {p.class_year}</span>}
                        {p.high_school && <span className="truncate">• {p.high_school}</span>}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div style={{ color: gpaColor(p.gpa), fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} className="text-xl leading-none">
                        {p.gpa ? parseFloat(p.gpa).toFixed(2) : '—'}
                      </div>
                      <div className="text-[#546E7A] text-[9px] uppercase tracking-wide mt-1">GPA</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: eligBg(p.ncaa_eligibility),
                      border: `1px solid ${eligColor(p.ncaa_eligibility)}40`,
                      color: eligColor(p.ncaa_eligibility),
                      fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                    }}>
                      {eligIcon(p.ncaa_eligibility)}
                      {eligLabel(p.ncaa_eligibility)}
                    </span>
                    {p.sat_score && <span className="text-[#546E7A] text-[10px]">SAT {p.sat_score}</span>}
                    {p.act_score && <span className="text-[#546E7A] text-[10px]">ACT {p.act_score}</span>}
                    {p.graduation_year && <span className="text-[#546E7A] text-[10px]">Grad {p.graduation_year}</span>}
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#546E7A]/20">
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/players/${p.id}/fit?tab=profile`); }}
                      className="text-[#00D4FF] text-sm font-semibold"
                    >
                      View Profile →
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); removeFromWatchlist(p.id); }}
                      className="text-[#546E7A] p-1"
                      aria-label={`Remove ${p.name} from watchlist`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>

        <div style={{ color: '#546E7A', fontSize: 12, marginTop: 10 }}>
          Showing {filtered.length} of {total} prospects
        </div>

      </div>
    </div>
  );
};

export default AcademicTracking;
